import React, { useState } from 'react';
import {
  Box,
  CssBaseline,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Divider,
  Typography,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Language as LanguageIcon,
  Key as KeyIcon,
  Translate as TranslateIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  People as PeopleIcon,
  AssignmentInd as AssignmentIndIcon,
  PersonAdd as PersonAddIcon,
  Lock as LockIcon,
  List as ListIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { Outlet, Link } from 'react-router-dom';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    marginLeft: 0,
  },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const AdminLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(!isMobile);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(true);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const toggleLanguageMenu = () => {
    setLanguageMenuOpen(!languageMenuOpen);
  };

  const navItems = [
    {
      label: 'Dil Ayarları',
      icon: <LanguageIcon />,
      children: [
        { label: 'Diller', path: '/admin/languages', icon: <LanguageIcon /> },
        { label: 'Çeviri Anahtarları', path: '/admin/translation-keys', icon: <KeyIcon /> },
        { label: 'Çeviri Değerleri', path: '/admin/translation-values', icon: <TranslateIcon /> },
      ]
    },
    {
      label: 'Rol Yönetimi',
      icon: <PeopleIcon />, // You'll need to import PeopleIcon from @mui/icons-material
      children: [
        { label: 'Roller', path: '/admin/roles', icon: <AssignmentIndIcon /> },
        { label: 'Kullanıcı Rolleri', path: '/admin/user-roles', icon: <PersonAddIcon /> },
      ]
    },
    {
      label: 'Yetki Yönetimi',
      icon: <LockIcon />,
      children: [
        { label: 'Yetkiler', path: '/admin/rights', icon: <ListIcon /> },
        { label: 'Rol Yetki Atama', path: '/admin/role-rights', icon: <AssignmentIcon /> },
      ]
    },
  ];

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <CssBaseline />

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
          [theme.breakpoints.down('sm')]: {
            position: 'fixed',
            zIndex: theme.zIndex.drawer + 1,
          },
        }}
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
      >
        <DrawerHeader sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6" noWrap component="div" sx={{ ml: 2 }}>
            Admin Paneli
          </Typography>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />

        <List>
          {navItems.map((item) => (
            <React.Fragment key={item.label}>
              <ListItem disablePadding>
                <ListItemButton onClick={toggleLanguageMenu}>
                  <ListItemIcon>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                  {languageMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>
              </ListItem>

              {languageMenuOpen && item.children && item.children.map((child) => (
                <ListItem
                  key={child.label}
                  disablePadding
                  sx={{ pl: 4 }}
                >
                  <ListItemButton
                    component={Link}
                    to={child.path}
                    onClick={isMobile ? handleDrawerClose : undefined}
                    sx={{
                      '&.active': {
                        backgroundColor: theme.palette.action.selected,
                      },
                    }}
                  >
                    <ListItemIcon>
                      {child.icon}
                    </ListItemIcon>
                    <ListItemText primary={child.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </React.Fragment>
          ))}
        </List>
      </Drawer>

      {!open && (
        <Box sx={{
          position: 'fixed',
          left: 0,
          top: theme.mixins.toolbar.minHeight,
          zIndex: theme.zIndex.drawer,
          [theme.breakpoints.up('sm')]: {
            top: `calc(${theme.mixins.toolbar.minHeight}px + ${theme.spacing(1)})`
          }
        }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              ml: 1,
              backgroundColor: theme.palette.background.paper,
              boxShadow: 1,
              borderRadius: '0 4px 4px 0',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      <Main open={open} sx={{
        flexGrow: 1,
        p: 3,
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(2),
          marginLeft: 0,
        }
      }}>
        <Toolbar /> {/* For spacing under appbar */}
        <Box sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: 1,
          p: 3,
          minHeight: 'calc(100vh - 64px - 48px)',
          [theme.breakpoints.down('sm')]: {
            p: 2,
            minHeight: 'calc(100vh - 56px - 32px)',
          }
        }}>
          <Outlet />
        </Box>
      </Main>
    </Box>
  );
};

export default AdminLayout;